import React, { Fragment, useState, Children, useEffect, useCallback, useLayoutEffect } from 'react';
import { withRouter, Redirect, RouteComponentProps } from 'react-router-dom';
import { compose, isEmpty, map, prop, values } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';
import { useTranslation } from 'react-i18next';
import { History } from 'history';
import { BookingBuilderSearchBar as SearchBar } from 'containers/BookingBuilderSearchBar';
import { useCurrentWidth } from 'effects';
import {
  useTravelAgentByUuidQuery,
  useCompanyByUuidQuery,
  useMembershipsByCompanyQuery,
  useGetHotelByUuidQuery,
  useCurrentUserAssignedDestinationsQuery,
} from './useHotelContainerQueries';
import { useParsedBookingBuilderQueryParams } from './urlParamsUtils';
import { convertArrayToKeyValueObject, mapWithIndex } from 'utils';
import styled from 'styled-components';
import { pureUiTheme } from 'pureUi/pureUiTheme';
import qs from 'qs';
import * as HotelContainerStyles from './HotelContainer.styles';
import { Hotel } from 'components';
import SummaryForm from 'containers/SummaryForm';
import { makeBackendApi } from 'services/BackendApi';
import * as TableCardUI from 'pureUi/TableCard';
import { useSelector, useDispatch } from 'react-redux';
import * as BookingBuilderActions from 'store/modules/bookingBuilder/actions';
import * as BookingBuilderSelectors from 'store/modules/bookingBuilder/selectors';
import * as BookingBuilderSearchActions from 'store/modules/bookingBuilder/subdomains/search/actions';
import * as HotelActions from 'store/modules/hotel/actions';
import * as ProposalsActions from 'store/modules/proposals/actions';
import * as HotelAccommodationProductsActions from 'store/modules/hotelAccommodationProducts/actions';
import * as StaticRatesAvailabilityActions from 'store/modules/staticRatesAvailability/actions';
import { normalize } from 'normalizr';
import { hotel as hotelSchema } from 'api/schema';
import * as AgentsSelectors from 'store/modules/agents/selectors';
import * as AgentsActions from 'store/modules/agents/actions';
import * as AuthSelectors from 'store/modules/auth/selectors';
import * as HotelSelectors from 'store/modules/hotel/selectors';
import * as ProposalsSelectors from 'store/modules/proposals/selectors';
import * as ProposalsSelectorsV2 from 'store/modules/proposalsList/selectors';
import { IReduxDomainStatus, TCurrencyCode } from 'interfaces';
import { ProposalModal } from './ProposalModal';
import { VerticalSpace } from 'ui/VerticalSpace';
import { dateRangeChangeAction, destinationSetAction, saveOriginalQueryAction } from 'store/modules/fastSearch';
import { StickyTopBar } from './StickyTopBar';
import { ERoutingPreviousPage } from 'utils/routingUtils';
import * as BasketActions from 'store/modules/basket/actions';
import * as BasketSelectors from 'store/modules/basket/selectors';
import { isNil } from 'lodash-es';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import * as ActingOnBehalfOfActions from 'store/modules/actingOnBehalfOf/actions';
import { Loader } from 'components/Loader/Loader';
import { SimpleTabs } from 'ui/SimpleTabs';
import { useQueryClient } from '@tanstack/react-query';
import { AnnouncementsDisplay } from 'ui/AnnouncementsDisplay/AnnouncementsDisplay';
import { CurrencyConversionProvider } from 'context/CurrencyConversion/CurrencyConversionContext';
import { getBookingCurrency } from 'store/modules/bookings';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
interface ILeftColumnProps {
  id: string;
  lodgings?: any[];
}
const LeftColumn = (props: ILeftColumnProps) => {
  const { id, lodgings } = props;

  const hotel = useSelector(HotelSelectors.hotelSelector);
  const photos = useSelector(HotelSelectors.hotelPhotosSelector);

  if (hotel === null) {
    return null;
  }
  return (
    <div
      style={{
        flex: 1,
        paddingLeft: '14px',
        paddingRight: '14px',
      }}
    >
      <TableCardUI.TableCardNumberedBanner className="mb-4">
        <TableCardUI.TableCardNumberBannerNumber>1</TableCardUI.TableCardNumberBannerNumber>
        <TableCardUI.TableCardNumberBannerText>Select Room Type</TableCardUI.TableCardNumberBannerText>
      </TableCardUI.TableCardNumberedBanner>

      <Hotel {...hotel} id={id} photos={photos} lodgings={lodgings} />
    </div>
  );
};

interface IRightColumnProps extends IHotelSummaryProps {}
const RightColumn = (props: IRightColumnProps) => {
  const { id, t, history } = props;
  return (
    <div
      style={{
        flex: 1,
        paddingLeft: '14px',
        paddingRight: '14px',
      }}
    >
      <HotelSummary t={t} id={id} history={history} />
    </div>
  );
};

const renderBackButton = (t, handler) => {
  return <HotelContainerStyles.Back onClick={handler}>{t('labels.backToSearch')}</HotelContainerStyles.Back>;
};

const renderBrochure = ({ uuid, displayName, url }) => (
  <TableCardUI.TableCardRow key={uuid} $depth={3} className="table-card-row brochure-row">
    <span>{displayName}</span>
    <a href={url} target="_blank">
      {/* get app */}
      <svg
        className="text-brown-100 hover:text-teal-100"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.01562 18H18.9844V20.0156H5.01562V18ZM18.9844 9L12 15.9844L5.01562 9H9V3H15V9H18.9844Z"
          fill="currentColor"
        />
      </svg>
    </a>
  </TableCardUI.TableCardRow>
);

interface IHotelSummaryProps {
  id: string; // the hotel UUID, from the route params
  t: (s: string) => React.ReactNode;
  history: RouteComponentProps['history'];
}
const HotelSummary = (props: IHotelSummaryProps) => {
  const { history, t, id } = props;

  const dispatch = useDispatch();

  const selectedTa = useSelector(AgentsSelectors.selectedTaSelector);
  const proposalsV2 = useSelector(ProposalsSelectorsV2.proposalsSelector);
  const requestPending = useSelector(ProposalsSelectorsV2.requestPendingSelector);
  const proposals = convertArrayToKeyValueObject(proposalsV2, 'uuid', 'name');

  const hotel = useSelector(HotelSelectors.hotelSelector);
  const brochures = useSelector(HotelSelectors.hotelBrochuresSelector);
  const backendApi = makeBackendApi(selectedTa?.uuid);
  const proposalStatus = useSelector(ProposalsSelectors.getProposalsStatus) as IReduxDomainStatus;
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);

  const handleAddToProposalClick = () => {
    setIsProposalModalOpen(true);
  };

  const handleCreateNewProposalClick = (name, bookingId) => {
    dispatch(ProposalsActions.createNewProposal(name, bookingId, backendApi));
  };

  const handleOnSuccess = useCallback(() => history.push('/search/beta'), [history]);

  const handleOnClose = useCallback(() => setIsProposalModalOpen(false), []);

  const handleAddToProposal = useCallback(
    async (proposalUuid, bookingUuid) => {
      await dispatch(ProposalsActions.addToProposal(proposalUuid, bookingUuid));
    },
    [dispatch]
  );

  if (!hotel) {
    return null;
  }

  return (
    <aside id="aside">
      <TableCardUI.TableCardNumberedBanner className="mb-4">
        <TableCardUI.TableCardNumberBannerNumber>2</TableCardUI.TableCardNumberBannerNumber>
        <TableCardUI.TableCardNumberBannerText>Review Selections</TableCardUI.TableCardNumberBannerText>
      </TableCardUI.TableCardNumberedBanner>
      {isProposalModalOpen && (
        <ProposalModal
          proposals={proposals}
          hotelUuid={id}
          createNewProposal={handleCreateNewProposalClick}
          addToProposal={handleAddToProposal}
          isLoading={requestPending}
          proposalStatus={proposalStatus}
          onSuccess={handleOnSuccess}
          onClose={handleOnClose}
        />
      )}
      <SummaryForm
        id={id}
        showRoomImage={false}
        handleAddToProposalClick={handleAddToProposalClick}
        companyUuid={selectedTa?.companyUuid}
      />
      {!isEmpty(brochures) && (
        <TableCardUI.TableCardBox className="mt-4 brochures">
          <HotelContainerStyles.AsideDetails>
            {/* @ts-ignore Legacy */}
            <>
              <TableCardUI.TableCardRow $depth={3} $hasOriginalLineHeight={true}>
                <HotelContainerStyles.Title>{t('brochure_plural')}</HotelContainerStyles.Title>
              </TableCardUI.TableCardRow>
              {values(map(renderBrochure, brochures))}
            </>
          </HotelContainerStyles.AsideDetails>
        </TableCardUI.TableCardBox>
      )}
    </aside>
  );
};

interface IHotelFullLayoutProps {
  t: (s: string) => React.ReactNode;
  id: string; // the hotel UUID, from the route params
  history: RouteComponentProps['history'];
  onBackToSearchResults: () => void;
  onSearch: () => void;
  lodgings?: any[];
}

export const HotelFullLayout = (props: IHotelFullLayoutProps) => {
  const { t, id, history, onBackToSearchResults, onSearch, lodgings } = props;

  // Use the booking builder specific query params hook
  const queryParams = useParsedBookingBuilderQueryParams();
  const { selectedDates = [], startDate, endDate } = queryParams;

  const basketBuild = useSelector(BasketSelectors.basketBuildSelector);
  const shouldShowLeaveAlert = useSelector(BasketSelectors.shouldShowLeaveWarningInBookingBuilderSelector);

  const hotel = useSelector(HotelSelectors.hotelSelector);
  const [isSearchBarVisible, setSearchBarVisible] = useState(false);
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    dispatch(dateRangeChangeAction(selectedDates));
    dispatch(destinationSetAction(hotel?.name ?? ''));
    setSearchBarVisible(true);
  }, []);

  return (
    <Fragment>
      {isNil(basketBuild) && (
        <HotelContainerStyles.StyledBreadcrumbs
          // @ts-ignore TODO legacy, investigate
          links={[
            { label: renderBackButton(t, onBackToSearchResults) },
            { label: hotel?.name, to: `/hotels/${id}${window.location.search}` },
          ]}
        />
      )}
      {isSearchBarVisible && isNil(basketBuild) && (
        <StickyTopBar className="pt-5 pb-5">
          <div className="search-bar-container px-14px">
            {/* @ts-ignore */}
            <SearchBar isSearchIconVisible history={history} onSearch={onSearch} />
          </div>
        </StickyTopBar>
      )}

      <div className="px-14px mt-4">
        <AnnouncementsDisplay
          hotelUuid={id}
          checkInDate={startDate || null}
          checkOutDate={endDate || null}
          appearIn="SEARCH_PAGE"
        />
      </div>

      <VerticalSpace height="20px" />
      <HotelContainerStyles.Full>
        <div
          style={{
            width: '50%',
          }}
        >
          <LeftColumn id={id} lodgings={lodgings} />
        </div>
        <div
          style={{
            width: '50%',
          }}
        >
          <RightColumn t={t} id={id} history={history} />
        </div>
      </HotelContainerStyles.Full>
      <LeaveWithoutSavingModal
        title="You are currently editing a basket item, and have not updated the basket, so any changes have not been saved."
        when={shouldShowLeaveAlert}
        confirmButtonLabel="Leave"
        cancelButtonLabel="Cancel"
      />
    </Fragment>
  );
};

interface IHotelTabLayoutProps {
  t: (s: string) => React.ReactNode;
  id: string; // the hotel UUID, from the route params
  history: RouteComponentProps['history'];
  onBackToSearchResults: () => void;
  lodgings?: any[];
}

export const HotelTabLayout = (props: IHotelTabLayoutProps) => {
  const { t, id, history, onBackToSearchResults, lodgings } = props;

  const hotel = useSelector(HotelSelectors.hotelSelector);
  const queryParams = useParsedBookingBuilderQueryParams();
  const { startDate, endDate } = queryParams;

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    const handleSwitchToBooking = () => {
      setActiveTabIndex(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('switchToBookingTab', handleSwitchToBooking);
    return () => window.removeEventListener('switchToBookingTab', handleSwitchToBooking);
  }, []);

  return (
    <Fragment>
      <HotelContainerStyles.StyledBreadcrumbs
        // @ts-ignore TODO legacy, investigate
        links={[
          { label: renderBackButton(t, onBackToSearchResults) },
          { label: hotel?.name, to: `/hotels/${id}${window.location.search}` },
        ]}
      />
      <div className="px-14px mt-4">
        <AnnouncementsDisplay
          hotelUuid={id}
          checkInDate={startDate || null}
          checkOutDate={endDate || null}
          appearIn="SEARCH_PAGE"
        />
      </div>
      <SimpleTabs
        className="o:w-full"
        activeTabIndex={activeTabIndex}
        onTabChange={setActiveTabIndex}
        tabConfig={[
          {
            name: 'Rooms',
            title: 'Rooms',
            styles: 'font-hurmegeometric-sans',
            renderContent: () => {
              return (
                <div className="max-w-full">
                  <LeftColumn id={id} lodgings={lodgings} />
                </div>
              );
            },
          },
          {
            name: 'Booking',
            title: 'Booking',
            styles: 'font-hurmegeometric-sans',
            renderContent: () => {
              return (
                <div className="max-w-full">
                  <RightColumn id={id} t={t} history={history} />
                </div>
              );
            },
          },
        ]}
      />
    </Fragment>
  );
};

interface IHotelContainerProps extends RouteComponentProps {
  id: string; // the hotel UUID, from the route params
  className?: string; // comes from styled components
}

export const HotelContainer = (props: IHotelContainerProps) => {
  const { t } = useTranslation();
  const {
    id: routeId, // Hotel ID from route params (/hotels/:id) - for backward compatibility
    className,
    history,
    location,
  }: { id: string; className?: string; history: History.LocationState; location: any } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const hasPageLoadedFromBookingConfOrBasket =
    history.location.state?.comingFrom === ERoutingPreviousPage.BOOKING_CONFIRMATION_PAGE ||
    history.location.state?.comingFrom === ERoutingPreviousPage.BASKET_PAGE;
  const resetBookingBuilderState = () => {
    dispatch(BookingBuilderActions.resetBookingBuilderUiStateAction());
    // reset the hotel accommodation products
    dispatch(HotelAccommodationProductsActions.resetHotelAccommodationProductsStateAction());
    // clear the hotel data
    dispatch(HotelActions.clearHotelAction());
    // clear out the static rates availability data
    dispatch(StaticRatesAvailabilityActions.resetStaticRatesAvailabilityDomainAction());
    // clear out the booking builder domain
    dispatch(BookingBuilderActions.clearBookingBuilderAction());
    // Re-initialization will be handled by the useEffect after navigation with fresh data
  };
  const isTa = useSelector(AuthSelectors.isTAOrActingAsTa);
  const isSr = useSelector(AuthSelectors.isSr);
  const currentUserUuid = useSelector(AuthSelectors.getCurrentUserUuid) as string | null;
  const isInternalUserOrActingAsInternalUser = useSelector(AuthSelectors.isInternalUserOrActingAsInternalUser);

  // Parse all booking builder query parameters using the booking builder specific hook
  const queryParams = useParsedBookingBuilderQueryParams();

  const {
    selectedTravelAgentUuid: tauuid,
    selectedMembershipUuid,
    selectedHotelUuid,
    lodgings,
    companyUuid,
  } = queryParams;
  const hotelUuid = selectedHotelUuid || routeId;

  // always load the hotel
  const { data: hotelDataFromQuery, isLoading: isLoadingHotel } = useGetHotelByUuidQuery(hotelUuid);

  // SR destination restriction: fetch assigned destinations for the current SR user
  const { data: srAssignedDestinations, isLoading: isLoadingSrDestinations } = useCurrentUserAssignedDestinationsQuery(
    currentUserUuid,
    isSr
  );

  // if we're an internal user, load the selected TA
  // and if we're getting a selected TA, also get the company and memberships
  const { data: selectedTa, isLoading: isLoadingTa } = useTravelAgentByUuidQuery(
    isInternalUserOrActingAsInternalUser ? tauuid || null : null
  );
  const { data: companyData } = useCompanyByUuidQuery(companyUuid || selectedTa?.companyUuid || null);
  const { data: membershipsData, isLoading: isLoadingMemberships } = useMembershipsByCompanyQuery(
    companyUuid || selectedTa?.companyUuid || null
  );

  // Get selectedTa from Redux to ensure it's been synced before rendering child components
  const selectedTaFromRedux = useSelector(AgentsSelectors.selectedTaSelector);

  const hotel = useSelector(HotelSelectors.hotelSelector);
  const hotelError = useSelector(HotelSelectors.hotelFetchErrorSelector);

  const { isMobile } = useCurrentWidth();

  const hotelCurrency = useSelector(getBookingCurrency) as TCurrencyCode;
  const isPaymentCurrencyEnabled = ParameterService.getParameters().ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP === true;

  const selectedCompanyMembership = useSelector(AgentsSelectors.selectedCompanyMembershipSelector);

  useEffect(() => {
    window.history.replaceState({}, '');

    return () => {
      resetBookingBuilderState();
      // we're leaving the booking builder, so clear out any possible state re. baskets and acting on behalf of
      dispatch(BasketActions.setShouldShowLeaveWarningInBookingBuilderAction(false));
      dispatch(BasketActions.setBasketBuildAction(null));
      dispatch(ActingOnBehalfOfActions.setActingOnBehalfOfDataAction(null));
    };
  }, []);

  // all the data is now driven by the URL, so when we search, remove the queries and everything will load again
  const onSearch = () => {
    queryClient.removeQueries({ queryKey: ['bookingBuilder'] });
  };

  // Update Redux store with company, memberships, and TA data in correct order (company first, then memberships, then TA)
  useEffect(() => {
    // Set company first to avoid clearing TA
    if (companyData) {
      dispatch(AgentsActions.selectedCompanyChangeAction(companyData));
      dispatch(BookingBuilderSearchActions.selectedCompanyChangeAction(companyData));
    }
    // Sync memberships to Redux for backward compatibility with BookingBuilderSearchBar
    if (membershipsData && membershipsData.length > 0) {
      dispatch(AgentsActions.getMembershipsSuccessAction(membershipsData, false));
    }
    // Then set TA after company is set
    if (selectedTa) {
      dispatch(AgentsActions.selectedTaChangeAction(selectedTa));
      dispatch(BookingBuilderSearchActions.selectedTaChangeAction(selectedTa));
    }
  }, [selectedTa, companyData, membershipsData, dispatch]);

  // Handle membership selection from URL params using React Query data
  useEffect(() => {
    if (membershipsData && membershipsData.length > 0 && selectedMembershipUuid) {
      if (!selectedCompanyMembership) {
        const selectedMembership = membershipsData.find(membership => membership.uuid === selectedMembershipUuid);
        if (selectedMembership) {
          dispatch(AgentsActions.setSelectedCompanyMembershipAction(selectedMembership));
        }
      }
    }
  }, [membershipsData, selectedMembershipUuid, selectedCompanyMembership, dispatch]);

  useEffect(() => {
    if (hotelDataFromQuery) {
      dispatch(HotelActions.setHotelUuidAction(hotelDataFromQuery.uuid));
      const normalized = normalize(hotelDataFromQuery, hotelSchema);
      dispatch(HotelActions.fetchHotelSuccessAction(normalized));
    }
  }, [hotelDataFromQuery]);

  useEffect(() => {
    if (hasPageLoadedFromBookingConfOrBasket) {
      return;
    }

    // Membership selection is now handled by the dedicated useEffect above

    // if you're logged in as an internal user, we initialise WITH the selected ta's id
    // if you're logged in as a TA, we dont
    if (selectedTa) {
      dispatch(
        BookingBuilderActions.initializeBookingBuilderAction(
          hotelUuid,
          selectedTa.uuid,
          hotelDataFromQuery || undefined
        )
      );
    } else if (isTa) {
      dispatch(
        BookingBuilderActions.initializeBookingBuilderAction(hotelUuid, undefined, hotelDataFromQuery || undefined)
      );
    }
  }, [hotelUuid, selectedTa, hotelDataFromQuery, isTa, dispatch]);

  if (hotelError) {
    return <p>{hotelError}</p>;
  }

  // SR destination restriction: redirect if hotel destination is not in the SR's assigned destinations
  if (
    isSr &&
    !isLoadingSrDestinations &&
    srAssignedDestinations &&
    srAssignedDestinations.length > 0 &&
    hotelDataFromQuery?.countryCode &&
    !srAssignedDestinations.includes(hotelDataFromQuery.countryCode)
  ) {
    return <Redirect to="/filters" />;
  }

  const isPageLoading =
    isLoadingHotel ||
    isNilOrEmpty(hotel) ||
    (isSr && isLoadingSrDestinations) ||
    (isInternalUserOrActingAsInternalUser && (isLoadingTa || isNilOrEmpty(selectedTaFromRedux)));

  return (
    <div className={className}>
      {isPageLoading ? (
        <Loader isLoading={true} text={t('messages.gettingHotel')} />
      ) : (
        <CurrencyConversionProvider hotelCurrency={hotelCurrency} isEnabled={isPaymentCurrencyEnabled}>
        <div className="container mx-auto max-w-1280px">
          {isMobile && (
            <HotelTabLayout
              t={t}
              id={hotelUuid}
              history={history}
              onBackToSearchResults={() => {
                dispatch(BookingBuilderActions.backToSearchResultsAction());
              }}
              lodgings={lodgings}
            />
          )}
          {!isMobile && (
            <HotelFullLayout
              t={t}
              id={hotelUuid}
              history={history}
              onBackToSearchResults={() => {
                dispatch(BookingBuilderActions.backToSearchResultsAction());
              }}
              onSearch={onSearch}
              lodgings={lodgings}
            />
          )}
        </div>
        </CurrencyConversionProvider>
      )}
    </div>
  );
};

const ConnectedHotel = compose(withRouter)(HotelContainer);

export default styled(ConnectedHotel)`
  .table-card-row.brochure-row {
    display: flex;
    padding-bottom: 12px;
    padding-top: 12px;
    line-height: 14px;
    padding-left: 20px;
    font-size: 12px;

    @media (min-width: 768px) {
      padding-left: 40px;
    }
    text-transform: uppercase;
    color: #736a65;
    border-bottom: 1px solid ${pureUiTheme.colors.grayDark};

    &:nth-child(2) {
      padding-top: 0px;
    }

    span {
      flex: 1;
      &::before {
        content: '';
        border-color: transparent #111;
        border-style: solid;
        border-width: 3px 0 3px 4px;
        display: block;
        height: 0;
        width: 0;
        left: -10px;
        top: 10px;
        position: relative;
      }
    }

    a {
      display: block;
      margin-top: 4px;
      margin-bottom: -4px;
      color: ${pureUiTheme.colors.gold};

      &:hover {
        color: ${pureUiTheme.colors.teal};
      }
    }
  }
`;
